import type { ButtonHTMLAttributes, ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'subtle'
export type ButtonSize = 'md' | 'sm'

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  className?: string
  children: ReactNode
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-primary-strong text-brand-on-primary font-semibold shadow-brand hover:bg-brand-primary-hover',
  secondary:
    'bg-brand-surface text-brand-text font-medium border border-brand-border hover:bg-brand-bg-soft',
  subtle: 'text-brand-text-muted font-medium hover:text-brand-text',
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'rounded-[14px] py-2.5 px-5 text-sm',
  sm: 'rounded-xl py-2 px-4 text-xs',
}

export function Button({
  variant = 'primary',
  size = 'md',
  leadingIcon,
  trailingIcon,
  onClick,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${
        disabled
          ? 'opacity-40 cursor-not-allowed'
          : 'transition-colors cursor-pointer'
      } inline-flex items-center justify-center gap-2 ${className}`}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  )
}
